import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Modal, Table, Badge } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import './coldemailer.css';
import './coldemailer-styles.css';

// Kept in step with the status enum on the OutreachRecipient model
const c_StatusOptions = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'queued', label: 'Queued' },
	{ value: 'sent', label: 'Sent' },
	{ value: 'replied', label: 'Replied' },
	{ value: 'bounced', label: 'Bounced' },
	{ value: 'complained', label: 'Complained' },
	{ value: 'recycled', label: 'Recycled' },
	{ value: 'unsubscribed', label: 'Unsubscribed' },
	{ value: 'suppressed', label: 'Suppressed' },
	{ value: 'failed_permanent', label: 'Failed' }
];

// Matches the reason enum on the SuppressionList model
const c_SuppressionReasons = {
	unsubscribed: 'Unsubscribed via link',
	bounced: 'Bounced',
	complained: 'Marked as spam',
	manual: 'Manually suppressed'
};

const c_BadgeVariants = {
	pending: 'warning',
	queued: 'info',
	sent: 'primary',
	replied: 'success',
	bounced: 'danger',
	complained: 'danger',
	recycled: 'secondary',
	unsubscribed: 'dark',
	suppressed: 'danger',
	failed_permanent: 'danger'
};

const format_date_time = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
			' ' +
			date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const get_badge_variant = (status) => {
	if(status && c_BadgeVariants[status]) {
		return c_BadgeVariants[status];
	}

	return 'secondary';
};

function OutreachRecipientView() {
	const navigate = useNavigate();
	const { id } = useParams();

	const [outreach_recipient, set_outreach_recipient] = useState(null);
	const [content, set_content] = useState([]);
	const [scheduled_emails, set_scheduled_emails] = useState([]);
	const [memberships, set_memberships] = useState([]);
	const [is_suppressed, set_is_suppressed] = useState(false);
	const [suppression, set_suppression] = useState(null);
	const [form_values, set_form_values] = useState(null);
	const [loading, set_loading] = useState(true);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');
	const [saved_message, set_saved_message] = useState('');
	const [show_suppress_modal, set_show_suppress_modal] = useState(false);
	const [suppressing, set_suppressing] = useState(false);

	const build_form_values = (loaded_recipient) => {
		return {
			status: loaded_recipient.status ? loaded_recipient.status : 'pending'
		};
	};

	const load_outreach_recipient = async () => {
		set_loading(true);
		set_error('');

		try {
			const result = await coldemailer_api.get_outreach_recipient(id);

			set_outreach_recipient(result.outreach_recipient);
			set_form_values(build_form_values(result.outreach_recipient));
			set_content(result.content ? result.content : []);
			set_scheduled_emails(result.scheduled_emails ? result.scheduled_emails : []);
			set_memberships(result.memberships ? result.memberships : []);
			set_is_suppressed(result.is_suppressed ? true : false);
			set_suppression(result.suppression ? result.suppression : null);
		} catch (error) {
			console.log('OutreachRecipientView.load_outreach_recipient - ERROR: ', error);
			set_error('Failed to load the recipient.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		load_outreach_recipient();
	}, [id]);

	const on_field_change = (field_name) => {
		return (event) => {
			const value = event.target.value;
			set_saved_message('');
			set_form_values(function(previous) {
				return Object.assign({}, previous, { [field_name]: value });
			});
		};
	};

	const has_changes = () => {
		if(!outreach_recipient || !form_values) {
			return false;
		}

		return build_form_values(outreach_recipient).status !== form_values.status;
	};

	const handle_save = async () => {
		set_saving(true);
		set_error('');
		set_saved_message('');

		try {
			const updated = await coldemailer_api.update_outreach_recipient(id, {
				status: form_values.status
			});

			set_outreach_recipient(updated);
			set_form_values(build_form_values(updated));
			set_saved_message('Recipient saved.');
		} catch (error) {
			console.log('OutreachRecipientView.handle_save - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to save the recipient.';
			set_error(message);
		} finally {
			set_saving(false);
		}
	};

	// Reloads rather than navigating away, so the new suppressed state is visible
	const handle_suppress = async () => {
		set_suppressing(true);
		set_error('');

		try {
			await coldemailer_api.suppress_outreach_recipient(id);
			set_show_suppress_modal(false);
			await load_outreach_recipient();
		} catch (error) {
			console.log('OutreachRecipientView.handle_suppress - ERROR: ', error);
			set_error('Failed to suppress the recipient.');
			set_show_suppress_modal(false);
		} finally {
			set_suppressing(false);
		}
	};

	const handle_back = () => {
		navigate(coldemailer_config.campaign_route);
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">{outreach_recipient && outreach_recipient.email ? outreach_recipient.email : 'Outreach Recipient'}</h4>
					<p className="ce-subtitle">View this recipient's outreach history</p>
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={handle_back}>
						Back
					</Button>

					<Button
						variant="outline-danger"
						size="sm"
						onClick={function() { set_show_suppress_modal(true); }}
						disabled={saving || is_suppressed}
					>
						Suppress
					</Button>

					<Button
						size="sm"
						className="ce-button-create"
						onClick={handle_save}
						disabled={!has_changes() || saving || is_suppressed}
					>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</div>
			</div>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	// The reason matters during a send: an unsubscribe is the recipient's own choice,
	// a bounce is a dead address, and a manual entry is someone's decision worth tracing
	const render_suppressed_banner = () => {
		if(!is_suppressed) {
			return null;
		}

		return (
			<Alert variant="danger" className="ce-suppression-alert">
				<p className="ce-suppression-line">
					This address is on the suppression list. No further emails will be sent to them.
				</p>

				{suppression ? (
					<p className="ce-suppression-line">
						<strong>{c_SuppressionReasons[suppression.reason] ? c_SuppressionReasons[suppression.reason] : suppression.reason}</strong>
						{suppression.suppressed_at ? ' on ' + format_date_time(suppression.suppressed_at) : ''}
					</p>
				) : null}

				{suppression && suppression.notes ? (
					<p className="ce-suppression-line ce-suppression-notes">
						{suppression.notes}
					</p>
				) : null}
			</Alert>
		);
	};

	const render_detail_row = (label, value) => {
		return (
			<div className="ce-detail-row">
				<span className="ce-detail-label">{label}</span>
				<span className="ce-detail-value">{value}</span>
			</div>
		);
	};

	const render_details = () => {
		return (
			<div className="ce-panel">
				<div className="ce-detail-grid">
					{render_detail_row('Email', outreach_recipient.email ? outreach_recipient.email : '-')}
					{render_detail_row('Touches Sent', outreach_recipient.touch_count === null || outreach_recipient.touch_count === undefined ? '-' : outreach_recipient.touch_count)}
					{render_detail_row('Retries', outreach_recipient.retry_count === null || outreach_recipient.retry_count === undefined ? '-' : outreach_recipient.retry_count)}
					{render_detail_row('Last Sent', format_date_time(outreach_recipient.last_sent_at))}
					{render_detail_row('Replied', format_date_time(outreach_recipient.replied_at))}
					{render_detail_row('Recycle After', format_date_time(outreach_recipient.recycle_after))}
					{render_detail_row('Company', outreach_recipient.company_id ? outreach_recipient.company_id : 'Not linked')}
					{render_detail_row('Added', format_date_time(outreach_recipient.createdAt))}
				</div>

				<Form>
					<Form.Group className="ce-modal-field ce-field-status">
						<Form.Label className="ce-modal-label">Status</Form.Label>
						<Form.Select
							value={form_values.status}
							onChange={on_field_change('status')}
							disabled={saving || is_suppressed}
						>
							{c_StatusOptions.map(function(option) {
								return (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								);
							})}
						</Form.Select>
					</Form.Group>
				</Form>
			</div>
		);
	};

	const render_scheduled_email_row = (scheduled_email) => {
		return (
			<tr key={scheduled_email.id}>
				<td className="ce-se-col-touch">
					{scheduled_email.touch_number ? scheduled_email.touch_number : '-'}
				</td>
				<td className="ce-se-col-status">
					<Badge bg={get_badge_variant(scheduled_email.status)}>
						{scheduled_email.status ? scheduled_email.status : 'unknown'}
					</Badge>
				</td>
				<td className="ce-se-col-send">
					{format_date_time(scheduled_email.send_at)}
				</td>
				<td className="ce-se-col-sent">
					{format_date_time(scheduled_email.sent_at)}
				</td>
			</tr>
		);
	};

	const render_scheduled_emails = () => {
		return (
			<div className="ce-panel ce-emails-panel">
				<div className="ce-panel-header">
					<h5 className="ce-panel-title">Scheduled Emails</h5>
				</div>

				<Table striped bordered hover responsive variant="dark" className="ce-table">
					<thead>
						<tr>
							<th className="ce-se-col-touch">Touch</th>
							<th className="ce-se-col-status">Status</th>
							<th className="ce-se-col-send">Send At</th>
							<th className="ce-se-col-sent">Sent At</th>
						</tr>
					</thead>
					<tbody>
						{scheduled_emails.length === 0 ? (
							<tr>
								<td colSpan={4} className="ce-empty">Nothing scheduled.</td>
							</tr>
						) : scheduled_emails.map(render_scheduled_email_row)}
					</tbody>
				</Table>
			</div>
		);
	};

	const render_content_row = (outreach_content) => {
		return (
			<tr key={outreach_content.id}>
				<td className="ce-oc-col-touch">
					{outreach_content.touch_number ? outreach_content.touch_number : '-'}
				</td>
				<td className="ce-oc-col-subject">
					{outreach_content.subject ? outreach_content.subject : '-'}
				</td>
				<td className="ce-oc-col-created">
					{format_date_time(outreach_content.createdAt)}
				</td>
			</tr>
		);
	};

	const render_content = () => {
		return (
			<div className="ce-panel ce-emails-panel">
				<div className="ce-panel-header">
					<h5 className="ce-panel-title">Generated Content</h5>
				</div>

				<Table striped bordered hover responsive variant="dark" className="ce-table">
					<thead>
						<tr>
							<th className="ce-oc-col-touch">Touch</th>
							<th className="ce-oc-col-subject">Subject</th>
							<th className="ce-oc-col-created">Created</th>
						</tr>
					</thead>
					<tbody>
						{content.length === 0 ? (
							<tr>
								<td colSpan={3} className="ce-empty">No content generated yet.</td>
							</tr>
						) : content.map(render_content_row)}
					</tbody>
				</Table>
			</div>
		);
	};

	const render_memberships = () => {
		return (
			<div className="ce-panel ce-emails-panel">
				<div className="ce-panel-header">
					<h5 className="ce-panel-title">List Memberships</h5>
				</div>

				<Table striped bordered hover responsive variant="dark" className="ce-table">
					<thead>
						<tr>
							<th className="ce-lm-col-list">Email List</th>
							<th className="ce-lm-col-added">Added</th>
						</tr>
					</thead>
					<tbody>
						{memberships.length === 0 ? (
							<tr>
								<td colSpan={2} className="ce-empty">Not on any list.</td>
							</tr>
						) : memberships.map(function(membership) {
							return (
								<tr key={membership.id}>
									<td className="ce-lm-col-list">
										{membership.email_list_id ? membership.email_list_id : '-'}
									</td>
									<td className="ce-lm-col-added">
										{format_date_time(membership.createdAt)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</Table>
			</div>
		);
	};

	const render_suppress_modal = () => {
		return (
			<Modal show={show_suppress_modal} onHide={function() { if(!suppressing) { set_show_suppress_modal(false); } }} centered>
				<Modal.Header closeButton>
					<Modal.Title>Suppress Recipient</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<p>Suppress <strong>{outreach_recipient ? outreach_recipient.email : ''}</strong>?</p>
					<p>Any pending emails are cancelled, the address is added to the suppression list, and the recipient is marked suppressed. The record is kept so the address cannot be added again by accident.</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={function() { set_show_suppress_modal(false); }} disabled={suppressing}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_suppress} disabled={suppressing}>
						{suppressing ? 'Suppressing...' : 'Suppress'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_all = () => {
		if(loading) {
			return render_loading();
		}

		if(!outreach_recipient || !form_values) {
			return null;
		}

		return (
			<React.Fragment>
				{render_suppressed_banner()}
				{render_details()}
				{render_scheduled_emails()}
				{render_content()}
				{render_memberships()}
				{render_suppress_modal()}
			</React.Fragment>
		);
	};

	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root" style={root_style}>
			{render_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}
			{saved_message ? <Alert variant="success">{saved_message}</Alert> : null}

			{render_all()}
		</div>
	);
}

export default OutreachRecipientView;
